<template>
  <div class="cash-count-form">
    <div v-if="errorMessage" class="alert alert-danger py-2">
      {{ errorMessage }}
    </div>

    <!-- Info UX -->
    <div v-if="usesCashCountDetails" class="alert alert-info py-2 mb-3">
      Le billetage se fait <strong>en fin de service</strong> pour boucler la journée avant passation ou fermeture.
      Un seul billetage est autorisé par jour et par utilisateur.
    </div>

    <!-- Tableau des billets / pièces -->
    <div v-if="usesCashCountDetails" class="card border-0 shadow-sm mb-3">
      <div class="card-header bg-white border-0 py-2">
        <div class="d-flex justify-content-between align-items-center">
          <h6 class="mb-0">Détail des billets et pièces</h6>
          <small class="text-muted">
            Saisissez les quantités par valeur faciale.
          </small>
        </div>
      </div>
      <div class="card-body p-2">
        <div class="table-responsive">
          <table class="table table-sm table-bordered align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th style="width: 40%;">Libellé</th>
                <th style="width: 20%;">Valeur faciale</th>
                <th style="width: 20%;">Quantité</th>
                <th style="width: 20%;">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(line, index) in form.lines" :key="index">
                <td class="fw-semibold cash-count-denomination-label">
                  <i :class="denominationIconClass(line.label)" aria-hidden="true"></i>
                  <span>{{ line.label }}</span>
                </td>
                <td class="text-end">
                  {{ $format.currency(line.denomination || 0) }}
                </td>
                <td class="text-end">
                  <PrimevueInputNumber
                    :modelValue="line.quantity"
                    class="w-100"
                    :min="0"
                    :maxFractionDigits="0"
                    @update:modelValue="updateLineQuantity(index, $event)"
                  />
                </td>
                <td class="text-end">
                  {{ $format.currency(line.line_total) }}
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th colspan="3" class="text-end">Total compté</th>
                <th class="text-end">{{ $format.currency(totalCounted) }}</th>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- Commentaire -->
    <div class="mb-3">
      <label class="form-label small fw-semibold">Commentaire</label>
      <PrimevueTextarea
        v-model="form.comment"
        class="w-100"
        rows="4"
        autoResize
        placeholder="Anomalies, explications, passation, etc."
      />
    </div>

    <!-- Résumé -->
    <div v-if="usesCashCountDetails" class="row g-3 mb-3">
      <div class="col-md-4">
        <label class="form-label small fw-semibold">Montant compté</label>
        <div class="form-control form-control-sm bg-light">
          {{ $format.currency(totalCounted) }}
        </div>
      </div>
      <div class="col-md-4">
        <label class="form-label small fw-semibold">Différence (compté - théorique)</label>
        <div
          class="form-control form-control-sm"
          :class="{
            'text-success': difference > 0,
            'text-danger': difference < 0,
            'text-muted': difference === 0,
          }"
        >
          {{ $format.currency(difference) }}
        </div>
      </div>
      <div class="col-md-4 d-flex align-items-end">
        <small class="text-muted">
          Surplus si positif, manquant si négatif, 0 si caisse conforme.
        </small>
      </div>
    </div>

    <!-- Boutons -->
    <div class="d-flex justify-content-end gap-2 mt-3">
      <button
        type="button"
        class="btn btn-outline-secondary btn-sm"
        @click="$emit('cancel')"
        :disabled="isSubmitting"
      >
        <i class="fa fa-xmark me-1" aria-hidden="true"></i>
        Annuler
      </button>
      <AppLoadingButton
        type="button"
        class="btn btn-primary btn-sm"
        :loading="isSubmitting"
        :loading-label="submittingLabel"
        @click="handleSubmit"
      >
        <i class="fa fa-save me-1" aria-hidden="true"></i>
        {{ submitLabel }}
      </AppLoadingButton>
    </div>

  </div>
</template>

<script>

export default {
  name: 'CashCountForm',
  props: {
    mode: {
      type: String,
      required: true, // 'create' | 'edit'
    },
    cashCount: {
      type: Object,
      default: null,
    },
    initialExpectedAmount: {
      type: Number,
      default: null,
    },
    initialOperationDate: {
      type: String,
      default: '',
    },
    cashRegisterSessionId: {
      type: Number,
      default: null,
    },
    cashRegisterName: {
      type: String,
      default: '',
    },
    initialOpeningAmount: {
      type: Number,
      default: null,
    },
    initialStartAt: {
      type: String,
      default: '',
    },
    initialEndAt: {
      type: String,
      default: '',
    },
    finalizeOnCreate: {
      type: Boolean,
      default: false,
    },
    cashCountEnabled: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      form: {
        id: null,
        closing_mode: 'cash_count',
        operation_date: '',
        opening_amount: null,
        expected_amount: null,
        counted_amount: 0,
        comment: '',
        start_at: '',
        end_at: '',
        lines: [],
      },
      isSubmitting: false,
      errorMessage: '',
    };
  },
  computed: {
    isEditMode() {
      return this.mode === 'edit';
    },
    totalCounted() {
      if (!this.usesCashCountDetails) {
        return Number(this.form.expected_amount || 0);
      }

      return this.form.lines.reduce((sum, line) => {
        return sum + Number(line.line_total || 0);
      }, 0);
    },
    difference() {
      if (!this.form.expected_amount && this.form.expected_amount !== 0) {
        return 0;
      }
      return this.totalCounted - Number(this.form.expected_amount || 0);
    },
    hasSessionContext() {
      return Boolean(this.cashRegisterName || this.initialStartAt || this.initialOpeningAmount !== null);
    },
    requiresFinalConfirmation() {
      return this.finalizeOnCreate && !this.isEditMode;
    },
    usesCashCountDetails() {
      return this.form.closing_mode !== 'simple_closure' && this.cashCountEnabled;
    },
    confirmationHeader() {
      return this.usesCashCountDetails ? 'Confirmer la clôture' : 'Confirmer la clôture';
    },
    confirmationMessage() {
      if (!this.usesCashCountDetails) {
        return 'Êtes-vous sûr de vouloir clôturer votre journée ? Après confirmation, la clôture sera validée définitivement.';
      }

      return 'Êtes-vous sûr de vouloir clôturer votre journée ? Après confirmation, le billetage sera validé définitivement et vous ne pourrez plus le modifier.';
    },
    submitLabel() {
      if (this.isEditMode) return 'Mettre à jour';

      return this.finalizeOnCreate ? 'Clôturer ma journée' : 'Créer';
    },
    submittingLabel() {
      return this.finalizeOnCreate && !this.isEditMode ? 'Clôture...' : 'Enregistrement...';
    },
  },
  watch: {
    cashCount: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.initFromExisting(newVal);
        } else {
          this.initNew();
        }
      },
    },
  },
  methods: {
    initNew() {
      const today = this.initialOperationDate || new Date().toISOString().slice(0, 10);
      this.form = {
        id: null,
        closing_mode: this.cashCountEnabled ? 'cash_count' : 'simple_closure',
        operation_date: today,
        opening_amount: this.initialOpeningAmount ?? null,
        expected_amount: this.initialExpectedAmount,
        counted_amount: 0,
        comment: '',
        start_at: this.toDateTimeLocal(this.initialStartAt),
        end_at: this.toDateTimeLocal(this.initialEndAt),
        lines: this.cashCountEnabled ? this.defaultLines() : [],
      };
    },

    initFromExisting(c) {
      this.form.id = c.id;
      this.form.closing_mode = c.closing_mode || 'cash_count';
      this.form.operation_date = (c.operation_date || '').slice(0, 10);

      this.form.opening_amount = c.opening_amount ?? null;
      this.form.expected_amount = c.expected_amount ?? null;
      this.form.counted_amount = c.counted_amount ?? 0;
      this.form.comment = c.comment || '';

      this.form.start_at = this.toDateTimeLocal(c.start_at);
      this.form.end_at = this.toDateTimeLocal(c.end_at);

      const lines = c.lines || [];
      if (this.form.closing_mode === 'simple_closure') {
        this.form.lines = [];
      } else if (lines.length > 0) {
        this.form.lines = lines.map(line => ({
          label: line.label,
          denomination: Number(line.denomination),
          quantity: Number(line.quantity),
          line_total: Number(line.line_total),
        }));
      } else {
        this.form.lines = this.defaultLines();
      }
    },

    defaultLines() {
      // Valeurs faciales classiques (FCFA) - tu peux ajuster selon ton contexte
      const denominations = [
        { label: 'Billet 10 000', value: 10000 },
        { label: 'Billet 5 000', value: 5000 },
        { label: 'Billet 2 000', value: 2000 },
        { label: 'Billet 1 000', value: 1000 },
        { label: 'Billet 500', value: 500 },
        { label: 'Pièce 500', value: 500 },
        { label: 'Pièce 200', value: 200 },
        { label: 'Pièce 100', value: 100 },
        { label: 'Pièce 50', value: 50 },
        { label: 'Pièce 25', value: 25 },
        { label: 'Pièce 10', value: 10 },
        { label: 'Pièce 5', value: 5 },
      ];

      return denominations.map(d => ({
        label: d.label,
        denomination: d.value,
        quantity: 0,
        line_total: 0,
      }));
    },

    toDateTimeLocal(val) {
      if (!val) return '';
      const date = val instanceof Date ? val : new Date(val);
      if (Number.isNaN(date.getTime())) return '';

      const pad = (number) => String(number).padStart(2, '0');

      return [
        date.getFullYear(),
        pad(date.getMonth() + 1),
        pad(date.getDate()),
      ].join('-')
        + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },

    recalculateLine(index) {
      const line = this.form.lines[index];
      const denom = Number(line.denomination || 0);
      const qty = Number(line.quantity || 0);
      line.line_total = denom * qty;
    },

    updateLineQuantity(index, value) {
      const line = this.form.lines[index];
      if (!line) return;

      line.quantity = Number(value || 0);
      this.recalculateLine(index);
    },

    denominationIconClass(label) {
      return String(label || '').toLowerCase().startsWith('pièce')
        ? 'fa fa-coins text-warning'
        : 'fa fa-money-bill-wave text-success';
    },

    async handleSubmit() {
      if (this.isSubmitting) return;

      this.errorMessage = '';
      const payload = this.buildPayload();

      if (this.requiresFinalConfirmation) {
        const confirmed = await this.$confirmAction({
          header: this.confirmationHeader,
          message: this.confirmationMessage,
          icon: 'fa fa-triangle-exclamation text-warning',
          acceptLabel: 'Oui, clôturer ma journée',
          rejectLabel: 'Annuler',
          acceptClass: 'p-button-danger',
        });

        if (!confirmed) return;
      }

      await this.submitPayload(payload);
    },

    buildPayload() {
      if (!this.usesCashCountDetails) {
        return {
          ...(this.cashRegisterSessionId ? { cash_register_session_id: this.cashRegisterSessionId } : {}),
          closing_mode: 'simple_closure',
          operation_date: this.form.operation_date,
          opening_amount: this.form.opening_amount,
          expected_amount: this.form.expected_amount,
          comment: this.form.comment || null,
          start_at: this.form.start_at || null,
          end_at: this.form.end_at || null,
        };
      }

      this.form.lines.forEach((line, idx) => this.recalculateLine(idx));
      const counted = this.totalCounted;

      return {
        ...(this.cashRegisterSessionId ? { cash_register_session_id: this.cashRegisterSessionId } : {}),
        closing_mode: 'cash_count',
        operation_date: this.form.operation_date,
        opening_amount: this.form.opening_amount,
        expected_amount: this.form.expected_amount,
        counted_amount: counted,
        comment: this.form.comment || null,
        start_at: this.form.start_at || null,
        end_at: this.form.end_at || null,
        lines: this.form.lines.map(line => ({
          label: line.label,
          denomination: Number(line.denomination || 0),
          quantity: Number(line.quantity || 0),
        })),
      };
    },

    async submitPayload(payload) {
      this.isSubmitting = true;
      this.errorMessage = '';

      try {
        let response = null;

        if (this.isEditMode && this.form.id) {
          response = await this.$api.put(`/cash-counts/${this.form.id}`, payload);
        } else {
          response = await this.$api.post('/cash-counts', payload);
        }

        if (this.requiresFinalConfirmation) {
          const cashCount = response?.data?.data || response?.data || {};

          if (!cashCount.id) {
            throw new Error('Billetage créé, mais impossible de retrouver son identifiant pour le valider.');
          }

          await this.$api.post(`/cash-counts/${cashCount.id}/validate`);
        }

        this.$emit('saved');
      } catch (error) {
        console.error('Erreur enregistrement billetage', error);
        if (error.response && error.response.data && error.response.data.errors) {
          const errors = error.response.data.errors;
          this.errorMessage = Object.values(errors).flat().join(' ');
        } else {
          this.errorMessage = 'Une erreur est survenue lors de la sauvegarde du billetage.';
        }
      } finally {
        this.isSubmitting = false;
      }
    },

  },
};
</script>

<style scoped>
.cash-count-form :deep(.p-inputnumber) {
  width: 100%;
}

.cash-count-form :deep(.p-inputnumber-input),
.cash-count-form :deep(.p-inputtext),
.cash-count-form .form-control-sm {
  border-radius: 0.25rem;
  font-size: 0.875rem;
  min-height: 31px;
  padding: 0.25rem 0.5rem;
}

.cash-count-form :deep(textarea.p-inputtextarea) {
  min-height: 124px;
}

.cash-count-denomination-label {
  align-items: center;
  display: inline-flex;
  gap: 0.45rem;
}
</style>
